import { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, MarkerF, PolylineF, useJsApiLoader } from "@react-google-maps/api";
import { IconCurrentLocation, IconMapOff } from "@tabler/icons-react";
import { MAPS_API_KEY } from "../config/env";
import { useI18n } from "../i18n";
import { pointOf, type Phase, type StopView } from "../lib/trip";
import type { DriverPosition, LatLng, TripData } from "../types/trip";

interface LiveMapProps {
  trip: TripData;
  phase: Phase;
  stops: StopView[];
  driver: DriverPosition | null;
  route: LatLng[];
}

const BRAND = "#8E0B13";
const INK = "#292F30";
const DAR_ES_SALAAM: LatLng = { lat: -6.7924, lng: 39.2083 };

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function useAnimatedPosition(target: DriverPosition | null): DriverPosition | null {
  const [shown, setShown] = useState<DriverPosition | null>(target);
  const frame = useRef<number | null>(null);
  const current = useRef<DriverPosition | null>(target);

  useEffect(() => {
    if (!target) return;
    const from = current.current;
    if (!from || prefersReducedMotion()) {
      current.current = target;
      setShown(target);
      return;
    }
    const start = performance.now();
    const duration = 1000;
    const step = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = {
        lat: from.lat + (target.lat - from.lat) * eased,
        lng: from.lng + (target.lng - from.lng) * eased,
        heading: target.heading,
      };
      current.current = next;
      setShown(next);
      if (p < 1) frame.current = requestAnimationFrame(step);
    };
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(step);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [target]);

  return shown;
}

export function LiveMap({ trip, phase, stops, driver, route }: LiveMapProps) {
  const { t } = useI18n();
  const { isLoaded, loadError } = useJsApiLoader({
    id: "safiri-tracker-maps",
    googleMapsApiKey: MAPS_API_KEY,
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const car = useAnimatedPosition(driver);

  const stopPoints = useMemo(
    () =>
      (trip.initialTripRoute ?? []).map((stop, index) => ({
        point: pointOf(stop),
        view: stops[index],
      })),
    [trip.initialTripRoute, stops]
  );

  const showDriver =
    !!driver && phase !== "completed" && phase !== "cancelled" && phase !== "noDriver";

  const frameTargets = useMemo(() => {
    const points: LatLng[] = [];
    if (showDriver && driver) points.push(driver);
    for (const { point, view } of stopPoints) {
      if (!point) continue;
      if (phase === "toPickup" || phase === "atPickup") {
        if (view?.kind === "pickup") points.push(point);
      } else if (view?.state !== "done" || phase === "completed" || phase === "finding") {
        points.push(point);
      }
    }
    if (points.length < 2) {
      for (const { point } of stopPoints) if (point) points.push(point);
    }
    return points;
  }, [stopPoints, phase, showDriver, driver]);

  const initialCenter = useRef<LatLng>(frameTargets[0] ?? DAR_ES_SALAAM);

  const fit = () => {
    if (!map || frameTargets.length === 0) return;
    if (frameTargets.length === 1) {
      map.panTo(frameTargets[0]);
      map.setZoom(16);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    frameTargets.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 56);
  };

  // Re-frame when the map first loads, the trip changes phase, or the route
  // is (re)drawn — not on every position update, so viewers can pan freely.
  const hasDriver = !!driver;
  useEffect(fit, [map, phase, route.length, hasDriver]);

  if (loadError || !MAPS_API_KEY) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-neutral-100 p-6 text-center text-sm text-neutral-600">
        <IconMapOff size={28} aria-hidden="true" />
        <p className="max-w-xs">{t("mapUnavailable")}</p>
      </div>
    );
  }
  if (!isLoaded) {
    return <div className="h-full w-full animate-pulse bg-neutral-200" aria-hidden="true" />;
  }

  const maps = google.maps;

  return (
    <div className="relative h-full w-full" role="region" aria-label={t("route")}>
      <GoogleMap
        mapContainerClassName="h-full w-full"
        onLoad={setMap}
        onUnmount={() => setMap(null)}
        center={initialCenter.current}
        zoom={14}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
          clickableIcons: false,
          zoomControlOptions: { position: maps.ControlPosition.RIGHT_CENTER },
        }}
      >
        {route.length > 1 && (
          <PolylineF
            path={route}
            options={{ strokeColor: BRAND, strokeOpacity: 0.9, strokeWeight: 5, zIndex: 5 }}
          />
        )}

        {stopPoints.map(({ point, view }, index) => {
          if (!point || !view) return null;
          const isEnd = view.kind !== "stop";
          const faded = view.state === "done" || view.state === "skipped";
          return (
            <MarkerF
              key={`stop-${index}`}
              position={point}
              zIndex={10}
              label={
                view.kind === "stop"
                  ? { text: String(index), color: "#FFFFFF", fontSize: "11px", fontWeight: "700" }
                  : undefined
              }
              icon={{
                path: maps.SymbolPath.CIRCLE,
                scale: isEnd ? 9 : 11,
                fillColor: view.kind === "pickup" ? "#16A34A" : view.kind === "dropoff" ? BRAND : INK,
                fillOpacity: faded ? 0.45 : 1,
                strokeColor: "#FFFFFF",
                strokeWeight: 3,
              }}
              title={view.address}
            />
          );
        })}

        {showDriver && car && (
          <MarkerF
            position={car}
            zIndex={100}
            icon={{
              path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 6,
              fillColor: INK,
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
              rotation: car.heading ?? 0,
            }}
          />
        )}
      </GoogleMap>

      <button
        type="button"
        onClick={fit}
        className="absolute bottom-6 right-3 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-white text-ink shadow-md transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary lg:bottom-4"
        aria-label={t("recenter")}
        title={t("recenter")}
      >
        <IconCurrentLocation size={22} aria-hidden="true" />
      </button>
    </div>
  );
}
